// Piping engine — substitutes {{question.<key>}}, {{variable.<key>}}, and
// the unnamespaced {{<key>}} (looked up in responses first, then variables)
// in survey-author text. Designed to be cheap so it can run on every render.
//
// Token shape mirrors the main app's piping syntax so a survey authored
// once renders identically in builder preview, public link, and SDK shell.

// Matches three syntaxes (priority order): {{question.<key>}}, {{variable.<key>}},
// {{<key>}}. The unnamespaced form is the most common in author-written text.
const TOKEN_RE = /\{\{\s*(?:(question|variable)\.)?([\w-]+)\s*\}\}/g;

export interface PipingContext {
  responses: Record<string, unknown>;
  variables: Record<string, unknown>;
}

// Replace tokens in `text` using values from the context. Missing keys
// resolve to an empty string so partially-filled surveys don't show
// `{{firstName}}` literally — they just render whitespace where the value
// would go.
export function applyPiping(text: string | undefined | null, ctx: PipingContext): string {
  if (!text) return '';
  if (!text.includes('{{')) return text;
  return text.replace(TOKEN_RE, (_match, kind: string | undefined, key: string) => {
    if (kind === 'variable') return formatPipedValue(ctx.variables[key]);
    if (kind === 'question') return formatPipedValue(ctx.responses[key]);
    // Unnamespaced — check responses first, fall back to variables (matches
    // the main app's resolution order).
    const fromResponses = ctx.responses[key];
    if (fromResponses !== undefined && fromResponses !== null && fromResponses !== '') {
      return formatPipedValue(fromResponses);
    }
    return formatPipedValue(ctx.variables[key]);
  });
}

// Stringify the value for inline display. Arrays join with ", " (multi-choice
// answers), booleans become Yes/No (matches the YesNo field's labels),
// objects (signature data URLs, address blobs) fall back to JSON for the
// rare survey author who pipes a structured response.
function formatPipedValue(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number' || typeof value === 'string') return String(value);
  if (Array.isArray(value)) return value.map((entry) => formatPipedValue(entry)).join(', ');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return '';
}

// Convenience: returns true when the input contains at least one token.
// Components can use this to skip the regex pass on the common case
// (no tokens at all) — useful when the text is rendered inside a hot loop.
export function containsPiping(text: string | undefined | null): boolean {
  return Boolean(text && text.includes('{{'));
}
